import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch, formatDateOnly } from "./api";

const serviceTypes = ["AMC", "Repair", "Installation", "Generator", "Panels", "Supply"];
const salesStatuses = ["Open", "Quotation in Progress", "Quote Sent", "Negotiation", "Closed", "Lost"];
const clientValueUsers = new Set([
  "kandarp.g@globusafriventures.com",
  "radhey.g@globusafriventures.com",
  "paschal.a@globusafriventures.com"
]);

const emptySale = {
  site_address: "",
  service_type: "AMC",
  requirement_description: "",
  estimated_value: "",
  technical_site_visit_required: "No",
  site_visit_date: "",
  site_visit_done_by: "",
  expected_closure_date: "",
  status: "Open",
  quotation_sent: "No",
  quotation_number: "",
  quotation_amount: "",
  remarks: ""
};

function ClientSales({ user }) {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [sales, setSales] = useState([]);
  const [draft, setDraft] = useState(emptySale);
  const canViewValues = clientValueUsers.has((user?.email || "").toLowerCase());

  function loadAll() {
    apiFetch(`/api/clients/${id}`).then(res => res.json()).then(setClient);
    apiFetch(`/api/clients/${id}/sales`)
      .then(res => res.json())
      .then(data => setSales(Array.isArray(data) ? data : []));
  }

  useEffect(() => {
    loadAll();
  }, [id]);

  function updateDraft(field, value) {
    setDraft(prev => ({ ...prev, [field]: value }));
  }

  function startNew() {
    setDraft(emptySale);
  }

  function saveSale() {
    apiFetch(`/api/clients/${id}/sales`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draft)
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          alert(data.error);
          return;
        }
        startNew();
        loadAll();
      });
  }

  return (
    <div style={page}>
      <button onClick={() => (window.location.href = `/clients/${id}`)}>Back to Client</button>
      <h2>Sales Lifecycle{client?.name ? ` - ${client.name}` : ""}</h2>

      <div style={card}>
        <h3>Start Sales / RFP Lifecycle</h3>
        <SaleForm draft={draft} updateDraft={updateDraft} canViewValues={canViewValues} />
        <button onClick={saveSale}>Create Sale</button>
      </div>

      <table border="1" cellPadding="8" style={table}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Service</th>
            <th>Status</th>
            <th>Site Address</th>
            {canViewValues && <th>Estimated Value</th>}
            <th>Expected Closure</th>
            <th>Quotation Number</th>
            {canViewValues && <th>Quotation Amount</th>}
            <th>Remarks</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {sales.length > 0 ? sales.map(sale => (
            <tr key={sale.id}>
              <td>{displayTrackingId(sale, client)}</td>
              <td>{sale.service_type}</td>
              <td>{sale.status}</td>
              <td>{sale.site_address}</td>
              {canViewValues && <td>{sale.estimated_value}</td>}
              <td>{formatDateOnly(sale.expected_closure_date)}</td>
              <td>{sale.quotation_sent === "Yes" ? sale.quotation_number || "" : "No"}</td>
              {canViewValues && <td>{sale.quotation_sent === "Yes" ? sale.quotation_amount || "" : ""}</td>}
              <td>{sale.remarks}</td>
              <td>
                <button onClick={() => (window.location.href = `/clients/${id}/sales/${sale.id}`)}>
                  Open Details
                </button>
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan={canViewValues ? "10" : "8"}>No sales lifecycle records yet</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function compactCode(value, length) {
  const cleaned = String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  return cleaned.slice(0, length) || "GEN";
}

function displayTrackingId(sale, client) {
  if (sale.tracking_id || sale.trackingId) return sale.tracking_id || sale.trackingId;
  if (!sale.id) return "";
  const sequence = String(Number(sale.id) + 320).padStart(6, "0");
  return `GAVL-${compactCode(sale.service_type, 3)}-${compactCode(client?.name, 6)}-${sequence}`;
}

function SaleForm({ draft, updateDraft, canViewValues }) {
  return (
    <div style={formGrid}>
      <Field label="Site Address">
        <input placeholder="Site address" value={draft.site_address || ""} onChange={e => updateDraft("site_address", e.target.value)} />
      </Field>
      <Field label="Service Type">
        <select value={draft.service_type || "AMC"} onChange={e => updateDraft("service_type", e.target.value)}>
          {serviceTypes.map(type => <option key={type} value={type}>{type}</option>)}
        </select>
      </Field>
      <Field label="Description of Requirement">
        <textarea placeholder="Description of requirement" value={draft.requirement_description || ""} onChange={e => updateDraft("requirement_description", e.target.value)} />
      </Field>
      {canViewValues && (
        <Field label="Estimated Value">
          <input placeholder="Estimated value" value={draft.estimated_value || ""} onChange={e => updateDraft("estimated_value", e.target.value)} />
        </Field>
      )}
      <Field label="Technical Site Visit Required">
        <select value={draft.technical_site_visit_required || "No"} onChange={e => updateDraft("technical_site_visit_required", e.target.value)}>
          <option value="No">No</option>
          <option value="Yes">Yes</option>
        </select>
      </Field>
      {draft.technical_site_visit_required === "Yes" && (
        <>
          <Field label="Site Visit Date">
            <input type="date" value={draft.site_visit_date || ""} onChange={e => updateDraft("site_visit_date", e.target.value)} />
          </Field>
          <Field label="Site Visit Done By">
            <input placeholder="Site visit done by" value={draft.site_visit_done_by || ""} onChange={e => updateDraft("site_visit_done_by", e.target.value)} />
          </Field>
        </>
      )}
      <Field label="Expected Closure Date">
        <input type="date" value={draft.expected_closure_date || ""} onChange={e => updateDraft("expected_closure_date", e.target.value)} />
      </Field>
      <Field label="Sales Status">
        <select value={draft.status || "Open"} onChange={e => updateDraft("status", e.target.value)}>
          {salesStatuses.map(status => <option key={status} value={status}>{status}</option>)}
        </select>
      </Field>
      <Field label="Quotation Sent">
        <select value={draft.quotation_sent || "No"} onChange={e => updateDraft("quotation_sent", e.target.value)}>
          <option value="No">No</option>
          <option value="Yes">Yes</option>
        </select>
      </Field>
      {draft.quotation_sent === "Yes" && (
        <>
          <Field label="Quotation Number">
            <input placeholder="Quotation number" value={draft.quotation_number || ""} onChange={e => updateDraft("quotation_number", e.target.value)} />
          </Field>
          {canViewValues && (
            <Field label="Quotation Amount">
              <input placeholder="Quotation amount" value={draft.quotation_amount || ""} onChange={e => updateDraft("quotation_amount", e.target.value)} />
            </Field>
          )}
        </>
      )}
      <Field label="Remarks">
        <textarea placeholder="Remarks" value={draft.remarks || ""} onChange={e => updateDraft("remarks", e.target.value)} />
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={fieldLabel}>
      <span>{label}</span>
      {children}
    </label>
  );
}

const page = { padding: "20px" };
const card = { border: "1px solid #ddd", padding: "15px", margin: "20px 0", borderRadius: "8px", backgroundColor: "#f9f9f9" };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px", marginBottom: "12px" };
const table = { width: "100%", borderCollapse: "collapse" };
const fieldLabel = { display: "grid", gap: "4px", fontSize: "14px", fontWeight: "bold", textAlign: "left" };

export default ClientSales;
